import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Eye, ChevronRight, CheckCircle, Clock, XCircle, AlertCircle, Glasses, X, Image, Tag, Package, ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePolling } from '@/hooks/usePolling';
import config from '@/config';
import BBoxAnnotator from '@/components/BBoxAnnotationDialog';

// Lens tags configuration
const LENS_TAGS = [
  { tag: 'left_lens', color: '#3b82f6' },
  { tag: 'right_lens', color: '#10b981' },
  { tag: 'both_lenses', color: '#8b5cf6' },
  { tag: 'no_lens', color: '#ef4444' }
];

// Convert database bbox format to component format
const convertBBoxFromDB = (bboxData) => {
  if (!bboxData) return null;
  
  try {
    const parsed = typeof bboxData === 'string' ? JSON.parse(bboxData) : bboxData;
    
    // Database format: {x1_percent, y1_percent, x2_percent, y2_percent}
    // Component format: {x, y, width, height} (all in percentages)
    return {
      x: parsed.x1_percent,
      y: parsed.y1_percent,
      width: parsed.x2_percent - parsed.x1_percent,
      height: parsed.y2_percent - parsed.y1_percent
    };
  } catch (e) {
    console.error('Error parsing bbox data:', e);
    return null;
  }
};

// Convert component bbox format to database format
const convertBBoxToDB = (bbox) => {
  if (!bbox) return null;
  
  return {
    x1_percent: bbox.x,
    y1_percent: bbox.y,
    x2_percent: bbox.x + bbox.width,
    y2_percent: bbox.y + bbox.height
  };
};

// Record Detail Modal with BBoxAnnotator
function RecordDetailModal({ isOpen, onClose, recordId, onLockAcquired }) {
  const { authenticatedFetch, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordData, setRecordData] = useState(null);
  const [lockId, setLockId] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  
  const lockUpdateIntervalRef = useRef(null);

  // Update lock status every 5 seconds
  useEffect(() => {
    if (lockId && isOpen) {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }

      lockUpdateIntervalRef.current = setInterval(async () => {
        try {
          const response = await authenticatedFetch(
            `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/lock/${lockId}/update-status`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                status_message: `Reviewing lens recognition`,
                progress_percentage: 50
              })
            }
          );

          if (!response.ok) {
            console.error('Failed to update lock status');
          }
        } catch (err) {
          console.error('Error updating lock status:', err);
        }
      }, 5000);

      return () => {
        if (lockUpdateIntervalRef.current) {
          clearInterval(lockUpdateIntervalRef.current);
        }
      };
    }

    return () => {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }
    };
  }, [lockId, isOpen, authenticatedFetch]);

  useEffect(() => {
    if (isOpen && recordId) {
      setLoading(false);
      setError(null);
      setRecordData(null);
      setLockId(null);
      setIsCompleting(false);
      
      acquireLockAndFetchData();
    }
    
    return () => {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }
    };
  }, [isOpen, recordId]);

  const acquireLockAndFetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, acquire lock
      const lockResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/${recordId}/lock`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!lockResponse.ok) {
        const errorData = await lockResponse.json();
        throw new Error(errorData.detail || `Failed to lock node: ${lockResponse.status}`);
      }

      const lockData = await lockResponse.json();
      setLockId(lockData.lock_id);
      
      // Notify parent component that lock was acquired
      if (onLockAcquired) {
        onLockAcquired();
      }

      // Fetch the record detail
      const detailResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/${recordId}`
      );

      if (!detailResponse.ok) {
        throw new Error(`HTTP error! status: ${detailResponse.status}`);
      }

      const data = await detailResponse.json();
      
      // Fetch existing labels if label_job_id exists
      let existingLabels = [];
      if (data.validation_info?.label_job_id) {
        try {
          const labelsResponse = await authenticatedFetch(
            `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/${recordId}/labels`
          );
          
          if (labelsResponse.ok) {
            const labelsData = await labelsResponse.json();
            existingLabels = labelsData.labels || [];
            console.log('Fetched existing labels:', existingLabels);
          }
        } catch (err) {
          console.warn('Failed to fetch existing labels:', err);
        }
      }
      
      data.existingLabels = existingLabels;
      setRecordData(data);

    } catch (err) {
      console.error('Error acquiring lock or fetching record detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseLock = async () => {
    if (!recordId) return;
    
    if (lockUpdateIntervalRef.current) {
      clearInterval(lockUpdateIntervalRef.current);
    }
    
    if (lockId) {
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/${recordId}/release-lock`,
          {
            method: 'POST'
          }
        );

        if (!response.ok) {
          throw new Error('Failed to release lock');
        }
      } catch (err) {
        console.error('Error releasing lock:', err);
      }
    }
    
    onClose();
  };

  const handleBBoxAnnotatorComplete = async (result) => {
    if (!recordData || isCompleting) return;
    
    if (lockUpdateIntervalRef.current) {
      clearInterval(lockUpdateIntervalRef.current);
    }
    
    setIsCompleting(true);
    
    try {
      // Convert annotations to labels format for the API
      const updatedLabels = result.labels_data.annotations.map(ann => {
        const bboxDB = convertBBoxToDB(ann.bbox);
        
        return {
          label_kind: 'object_detection',
          tag: ann.tag,
          bbox_data: bboxDB,
          confidence: 1.0,
          comment: ann.comment,
          image_id: recordData.label_job_data?.image_id
        };
      });
      
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            node_id: recordId,
            label_job_id: recordData.validation_info.label_job_id,
            updated_labels: updatedLabels
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to complete review');
      }

      console.log('Review completed successfully');
      onClose();
      
    } catch (err) {
      console.error('Error completing review:', err);
      setError(err.message);
      setIsCompleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleReleaseLock}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              title="Release lock and close"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold">Lens Recognition Verification</h2>
              <p className="text-blue-100 text-sm mt-1">
                {loading ? 'Loading...' : 'Mark detected lenses or add missing ones'}
                {lockId && <span className="ml-2 text-xs opacity-75">(Lock active)</span>}
              </p>
            </div>
          </div>
          <button
            onClick={handleReleaseLock}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            title="Release lock and close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading || isCompleting ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">
                  {isCompleting ? 'Completing review...' : 'Loading...'}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <p className="text-red-600 font-semibold mb-2">Error</p>
                <p className="text-gray-600">{error}</p>
                <button
                  onClick={handleReleaseLock}
                  className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          ) : recordData && recordData.image_url ? (
            <BBoxAnnotator
              key={recordId}
              imageUrl={recordData.image_url}
              review_lock_id={lockId}
              review_id={recordId}
              onComplete={handleBBoxAnnotatorComplete}
              containerMode={true}
              tags={LENS_TAGS}
              initialAnnotations={recordData.existingLabels?.map((label, idx) => ({
                id: label.label_id || `existing_${idx}`,
                bbox: convertBBoxFromDB(label.bbox_data),
                tag: label.tag,
                comment: label.comment || ''
              })).filter(ann => ann.bbox !== null) || []}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-600">No data to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Rest of the component (LensRecVerificationTable) remains the same...
function LensRecVerificationTable() {
  const { authenticatedFetch } = useAuth();
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const observerTarget = useRef(null);
  const pageSize = 20;
  const refreshIntervalRef = useRef(null);

  const fetchRecords = useCallback(async (page) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const url = `${config.BACKEND_API_ADDRESS}/nodes/lensrec-verification/list?page=${page}&page_size=${pageSize}`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (page === 1) {
        setRecords(data.items);
      } else {
        setRecords(prev => [...prev, ...data.items]);
      }
      
      setTotalCount(data.total);
      setHasMore(page < data.total_pages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching records:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loading]);

  useEffect(() => {
    fetchRecords(1);
  }, []);

  // Auto-refresh every 5 seconds, but only while the modal is closed.
  // usePolling waits for each request
  // to settle before scheduling the next, so a slow endpoint can't make
  // requests pile up.
  usePolling(() => fetchRecords(1), 5000, !detailModalOpen);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchRecords(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage, fetchRecords]);

  const handleRefresh = () => {
    setRecords([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchRecords(1);
  };

  const handleViewRecordDetails = (record) => {
    if (record.status === 'running') {
      return;
    }

    setSelectedRecordId(record.node_id);
    setDetailModalOpen(true);
  };

  // E opens the first record that can actually be opened. Rows already locked
  // by someone else are 'running' and are skipped rather than counted, so the
  // key does the same thing as clicking the topmost available row.
  useEffect(() => {
    if (detailModalOpen) return undefined;
    const onKey = (event) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const target = event.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
                     || target.tagName === 'SELECT' || target.isContentEditable)) return;
      if (event.key.toLowerCase() !== 'e') return;
      const first = records.find((record) => record.status !== 'running');
      if (!first) return;
      event.preventDefault();
      handleViewRecordDetails(first);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailModalOpen, records]);

  const handleModalClose = () => {
    setDetailModalOpen(false);
    setSelectedRecordId(null);
    handleRefresh();
  };

  const handleLockAcquired = () => {
    fetchRecords(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'need_revision': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'need_revision': return <AlertCircle className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Glasses className="w-7 h-7 text-blue-600" />
                Lens Recognition Verification
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} records need verification
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading records</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {records.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Glasses className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  No records found
                </h3>
                <p className="text-gray-600">
                  No lens recognition records need verification at this time
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {records.map((record) => {
                const isLocked = record.status === 'running';

                return (
                  <div 
                    key={record.node_id}
                    className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
                      isLocked ? 'opacity-75' : ''
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Image Section */}
                        <div className="flex-shrink-0">
                          {record.image_url ? (
                            <div className="w-24 h-24 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors">
                              <img 
                                src={record.image_url} 
                                alt="Lens detection preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                              <Image className="w-10 h-10 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info Section */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-semibold text-gray-900">
                              Workflow: 
                            </span>
                            <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200 text-blue-700">
                              {record.workflow_id}
                            </code>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm mb-2">
                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                              {getStatusIcon(record.status)}
                              {record.status}
                              {isLocked && <Lock className="w-3 h-3 ml-1" />}
                            </span>
                            
                            <span className="text-gray-600">
                              Detected: <span className="font-medium">{record.validation_info.detection_count} lens(es)</span>
                            </span>
                          </div>
                          
                          {/* Failure Reason */}
                          <div className="text-sm text-red-600 flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            <span>{record.validation_info.validation_failure_reason}</span>
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <div className="flex-shrink-0">
                          <button
                            onClick={() => handleViewRecordDetails(record)}
                            disabled={isLocked}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                              isLocked
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            title={
                              isLocked 
                                ? 'This node is currently locked by another user' 
                                : 'Review and correct lens detection'
                            }
                          >
                            {isLocked ? (
                              <>
                                <Lock className="w-4 h-4" />
                                Locked
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4" />
                                Review
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Infinite Scroll Observer */}
          <div ref={observerTarget} className="py-4 text-center">
            {loading && (
              <div className="flex items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm text-gray-600">Loading more...</span>
              </div>
            )}
            {!hasMore && records.length > 0 && (
              <span className="text-sm text-gray-500">
                No more records to load
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Record Detail Modal */}
      <RecordDetailModal
        isOpen={detailModalOpen}
        onClose={handleModalClose}
        recordId={selectedRecordId}
        onLockAcquired={handleLockAcquired}
      />
    </div>
  );
}

export default LensRecVerificationTable;